import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Wallet, RefreshCw, TrendingUp, AlertCircle, CheckCircle, Clock,
  Users, DollarSign, Shield, Settings, Search, Filter, Eye,
  ChevronRight, X, Check, AlertTriangle, Sliders, BarChart3,
  ArrowUpRight, ArrowDownLeft, Building2, User, Zap, Ban,
  FileText, MessageSquare, Flag
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (ts) => ts ? new Date(ts).toLocaleString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

const STATUS_COLOR = {
  completed: "text-green-400 bg-green-500/10 border-green-500/20",
  pending:   "text-yellow-400 bg-yellow-500/10 border-yellow-500/20",
  disputed:  "text-orange-400 bg-orange-500/10 border-orange-500/20",
  resolved:  "text-blue-400 bg-blue-500/10 border-blue-500/20",
  failed:    "text-red-400 bg-red-500/10 border-red-500/20",
  refund:    "text-purple-400 bg-purple-500/10 border-purple-500/20",
};
const STATUS_LABEL = {
  completed: "Completado", pending: "Pendiente", disputed: "En Disputa",
  resolved: "Resuelto", failed: "Fallido", refund: "Reembolso",
};
const TYPE_LABEL = {
  transfer: "Transferencia", recharge: "Recarga", refund: "Reembolso",
  withdrawal: "Retiro", payment: "Pago",
};

function MetricCard({ icon: Icon, label, value, color = "text-red-400", bg = "bg-red-500/10", sub }) {
  return (
    <div className="glass-panel p-5">
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${bg}`}>
          <Icon size={16} className={color} />
        </div>
        <span className="text-gray-500 text-xs font-medium">{label}</span>
      </div>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      {sub && <p className="text-gray-600 text-xs mt-1">{sub}</p>}
    </div>
  );
}

function ConfigPanel({ onClose }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/wallet/config`).then(r => setCfg(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/wallet/config`, cfg);
      toast.success("Configuración guardada");
      onClose();
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  if (!cfg) return <div className="flex items-center justify-center p-12"><RefreshCw size={24} className="text-red-400 animate-spin" /></div>;

  const fields = [
    { key: "commission_user_to_user",     label: "Comisión Usuario → Usuario (%)" },
    { key: "commission_user_to_business", label: "Comisión Usuario → Negocio (%)" },
    { key: "commission_business_to_user", label: "Comisión Negocio → Usuario (%)" },
    { key: "min_transfer", label: "Transferencia Mínima ($MXN)" },
    { key: "max_transfer", label: "Transferencia Máxima ($MXN)" },
    { key: "min_recharge", label: "Recarga Mínima ($MXN)" },
    { key: "max_recharge", label: "Recarga Máxima ($MXN)" },
  ];

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="glass-panel w-full max-w-lg">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-purple-500/20 flex items-center justify-center">
              <Sliders size={16} className="text-purple-400" />
            </div>
            <h3 className="text-white font-semibold">Configuración de Comisiones</h3>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          {fields.map(f => (
            <div key={f.key}>
              <label className="block text-gray-400 text-xs mb-1.5 font-medium">{f.label}</label>
              <input
                type="number" step="0.01" value={cfg[f.key] || ""}
                onChange={e => setCfg({ ...cfg, [f.key]: parseFloat(e.target.value) || 0 })}
                className="input-glass w-full"
              />
            </div>
          ))}
        </div>
        <div className="p-6 pt-0 flex gap-3 justify-end">
          <button onClick={onClose} className="btn-secondary">Cancelar</button>
          <button onClick={save} disabled={saving} className="btn-primary flex items-center gap-2">
            {saving ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}

function DisputeModal({ dispute, onClose, onResolve }) {
  const [action, setAction] = useState("no_action");
  const [resolution, setResolution] = useState("");
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);

  const resolve = async () => {
    if (!resolution.trim()) { toast.error("Escribe la resolución"); return; }
    setSaving(true);
    try {
      await onResolve(dispute.id, { action, resolution, amount: amount ? parseFloat(amount) : null });
      onClose();
    } finally { setSaving(false); }
  };

  const tx = dispute.transaction;
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="glass-panel w-full max-w-lg">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-white/10">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Flag size={16} className="text-orange-400" />Resolver Disputa
          </h3>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="glass-panel-sm p-4 space-y-1.5">
            <p className="text-gray-400 text-xs font-medium uppercase tracking-wider">Transacción</p>
            {tx && <>
              <p className="text-white text-sm">Monto: <span className="text-red-400 font-semibold">${fmt(tx.amount)}</span></p>
              <p className="text-gray-400 text-xs">{tx.sender_wallet || tx.sender_id} → {tx.receiver_wallet || tx.receiver_id}</p>
              <p className="text-gray-400 text-xs">{fmtDate(tx.created_at)}</p>
            </>}
            <p className="text-gray-400 text-xs mt-2">Motivo: <span className="text-orange-300">{dispute.reason}</span></p>
          </div>
          <div>
            <label className="block text-gray-400 text-xs mb-2 font-medium">Acción</label>
            <div className="flex gap-2">
              {[
                { v: "no_action", label: "Sin acción" },
                { v: "refund", label: "Reembolso total" },
                { v: "partial_refund", label: "Reembolso parcial" },
              ].map(opt => (
                <button key={opt.v} onClick={() => setAction(opt.v)}
                  className={`flex-1 py-2 rounded-xl text-xs font-medium transition-colors border ${action === opt.v ? "bg-red-500/20 border-red-500/40 text-red-400" : "bg-white/5 border-white/10 text-gray-400 hover:bg-white/10"}`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          {action === "partial_refund" && (
            <div>
              <label className="block text-gray-400 text-xs mb-1.5 font-medium">Monto a reembolsar ($MXN)</label>
              <input type="number" value={amount} onChange={e => setAmount(e.target.value)} className="input-glass w-full" placeholder="0.00" />
            </div>
          )}
          <div>
            <label className="block text-gray-400 text-xs mb-1.5 font-medium">Resolución / Nota interna</label>
            <textarea value={resolution} onChange={e => setResolution(e.target.value)}
              className="input-glass w-full resize-none" rows={3} placeholder="Describe la decisión tomada..." />
          </div>
        </div>
        <div className="p-6 pt-0 flex gap-3 justify-end">
          <button onClick={onClose} className="btn-secondary">Cancelar</button>
          <button onClick={resolve} disabled={saving} className="btn-primary flex items-center gap-2">
            {saving ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
            Resolver
          </button>
        </div>
      </div>
    </div>
  );
}

export default function OkWalletAdmin() {
  const [tab, setTab] = useState("overview");
  const [metrics, setMetrics] = useState(null);
  const [wallets, setWallets] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConfig, setShowConfig] = useState(false);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [txFilter, setTxFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [disputeFilter, setDisputeFilter] = useState("all");
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const results = await Promise.allSettled([
        axios.get(`${API}/admin/wallet/metrics`),
        axios.get(`${API}/admin/wallet/all`),
        axios.get(`${API}/admin/wallet/transactions?limit=200`),
        axios.get(`${API}/admin/wallet/disputes`),
    ]);
    const [m, w, t, d] = results;
    if (m.status === "fulfilled") setMetrics(m.value.data);
    if (w.status === "fulfilled") setWallets(w.value.data);
    if (t.status === "fulfilled") setTransactions(t.value.data);
    if (d.status === "fulfilled") setDisputes(d.value.data);
    const failure = results.find((result) => result.status === "rejected");
    setLoadError(failure?.reason?.response?.data?.detail?.message || failure?.reason?.response?.data?.detail || (failure ? "No se pudo consultar OkWallet. Reintenta en unos segundos." : null));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const approveRecharge = async (txId) => {
    try {
      await axios.post(`${API}/admin/wallet/approve-recharge/${txId}`);
      toast.success("Recarga aprobada");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const resolveDispute = async (disputeId, data) => {
    try {
      await axios.put(`${API}/admin/wallet/disputes/${disputeId}/resolve`, data);
      toast.success("Disputa resuelta");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const tabs = [
    { id: "overview", label: "Resumen", icon: BarChart3 },
    { id: "wallets", label: "Wallets", icon: Wallet },
    { id: "transactions", label: "Transacciones", icon: ArrowUpRight },
    { id: "disputes", label: `Disputas${disputes.filter(d => d.status === "open").length > 0 ? ` (${disputes.filter(d => d.status === "open").length})` : ""}`, icon: Flag },
  ];

  const filteredTxs = transactions.filter(t => {
    const matchStatus = txFilter === "all" || t.status === txFilter || t.type === txFilter;
    const matchSearch = !search || t.sender_wallet?.includes(search) || t.receiver_wallet?.includes(search) || t.id?.includes(search);
    return matchStatus && matchSearch;
  });
  const filteredDisputes = disputes.filter(d => disputeFilter === "all" || d.status === disputeFilter);

  if (loading && !metrics) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw size={28} className="text-red-400 animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Wallet size={20} className="text-red-400" />OkWallet Admin
          </h2>
          <p className="text-gray-500 text-sm mt-0.5">Gestión centralizada de carteras digitales</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
            <RefreshCw size={16} />
          </button>
          <button onClick={() => setShowConfig(true)} className="btn-secondary flex items-center gap-2 text-sm">
            <Sliders size={15} />Comisiones
          </button>
        </div>
      </div>

      {loadError && (
        <div className="mb-5 flex items-center gap-3 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3">
          <AlertTriangle size={17} className="text-red-400 shrink-0" />
          <p className="flex-1 text-sm text-red-200">{loadError}</p>
          <button onClick={load} className="btn-secondary text-xs py-1.5 px-3">Reintentar</button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto no-scrollbar">
        {tabs.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all flex-shrink-0 ${tab === t.id ? "bg-red-500/20 text-red-400 border border-red-500/30" : "text-gray-400 hover:bg-white/5 border border-white/5"}`}>
              <Icon size={15} />{t.label}
            </button>
          );
        })}
      </div>

      {/* Overview */}
      {tab === "overview" && metrics && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricCard icon={Wallet} label="Total Wallets" value={metrics.total_wallets} />
            <MetricCard icon={Users} label="Wallets Activas" value={metrics.active_wallets} color="text-green-400" bg="bg-green-500/10" />
            <MetricCard icon={ArrowUpRight} label="Transacciones" value={metrics.total_txs} color="text-blue-400" bg="bg-blue-500/10" />
            <MetricCard icon={DollarSign} label="Comisiones OkBot" value={`$${fmt(metrics.total_fees)}`} color="text-yellow-400" bg="bg-yellow-500/10" sub="MXN acumulados" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <MetricCard icon={Clock} label="Recargas Pendientes" value={metrics.pending_recharges} color="text-orange-400" bg="bg-orange-500/10" />
            <MetricCard icon={AlertTriangle} label="Disputas Abiertas" value={metrics.open_disputes} color="text-red-400" bg="bg-red-500/10" />
            <MetricCard icon={Shield} label="Saldo Total en Wallets" value={`$${fmt(metrics.total_balance)}`} color="text-purple-400" bg="bg-purple-500/10" sub="MXN circulando" />
          </div>

          {/* Pending recharges */}
          {transactions.filter(t => t.status === "pending" && t.type === "recharge").length > 0 && (
            <div className="glass-panel">
              <div className="p-4 border-b border-white/5 flex items-center gap-2">
                <Clock size={16} className="text-orange-400" />
                <h3 className="text-white font-medium text-sm">Recargas Pendientes de Aprobación</h3>
              </div>
              <div className="divide-y divide-white/5">
                {transactions.filter(t => t.status === "pending" && t.type === "recharge").map(tx => (
                  <div key={tx.id} className="p-4 flex items-center justify-between">
                    <div>
                      <p className="text-white text-sm font-medium">${fmt(tx.amount)} MXN</p>
                      <p className="text-gray-500 text-xs">Método: <span className="text-gray-300 capitalize">{tx.method}</span> · {fmtDate(tx.created_at)}</p>
                      {tx.wallet_id && <p className="text-gray-600 text-xs font-mono">{tx.wallet_id}</p>}
                    </div>
                    <button onClick={() => approveRecharge(tx.id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg text-xs font-medium transition-colors border border-green-500/30">
                      <CheckCircle size={13} />Aprobar
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Wallets */}
      {tab === "wallets" && (
        <div className="glass-panel overflow-x-auto">
          <table className="table-glass">
            <thead><tr>
              <th>OkWallet ID</th><th>Propietario</th><th>Tipo</th><th>Saldo</th><th>Estado</th><th>Creada</th>
            </tr></thead>
            <tbody>
              {wallets.map(w => (
                <tr key={w.id}>
                  <td><span className="font-mono text-xs text-red-400">{w.wallet_id}</span></td>
                  <td className="text-white text-sm">{w.username}</td>
                  <td>
                    <span className={`flex items-center gap-1 text-xs ${w.owner_type === "business" ? "text-purple-400" : "text-blue-400"}`}>
                      {w.owner_type === "business" ? <Building2 size={12} /> : <User size={12} />}
                      {w.owner_type === "business" ? "Negocio" : "Usuario"}
                    </span>
                  </td>
                  <td className="text-green-400 font-semibold">${fmt(w.balance)}</td>
                  <td>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${w.status === "active" ? "bg-green-500/10 text-green-400 border-green-500/20" : "bg-red-500/10 text-red-400 border-red-500/20"}`}>
                      {w.status === "active" ? "Activa" : "Suspendida"}
                    </span>
                  </td>
                  <td className="text-gray-500 text-xs">{fmtDate(w.created_at)}</td>
                </tr>
              ))}
              {wallets.length === 0 && (
                <tr><td colSpan={6} className="text-center text-gray-500 py-12">No hay wallets creadas</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Transactions */}
      {tab === "transactions" && (
        <div className="space-y-4">
          <div className="flex gap-3 flex-wrap">
            <div className="flex-1 min-w-0 relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por ID, wallet..."
                className="input-glass w-full pl-8 text-sm" />
            </div>
            <select value={txFilter} onChange={e => setTxFilter(e.target.value)} className="input-glass text-sm">
              <option value="all">Todos los tipos</option>
              <option value="transfer">Transferencias</option>
              <option value="recharge">Recargas</option>
              <option value="refund">Reembolsos</option>
              <option value="pending">Pendientes</option>
              <option value="disputed">En Disputa</option>
            </select>
          </div>
          <div className="glass-panel overflow-x-auto">
            <table className="table-glass">
              <thead><tr>
                <th>ID</th><th>Tipo</th><th>De</th><th>Para</th><th>Monto</th><th>Comisión</th><th>Estado</th><th>Fecha</th>
              </tr></thead>
              <tbody>
                {filteredTxs.map(tx => (
                  <tr key={tx.id}>
                    <td><span className="font-mono text-xs text-gray-500">{tx.id?.slice(0, 8)}...</span></td>
                    <td><span className="text-xs text-gray-300 capitalize">{TYPE_LABEL[tx.type] || tx.type}</span></td>
                    <td><span className="text-xs font-mono text-blue-400">{tx.sender_wallet || tx.sender_id?.slice(0, 8) || "—"}</span></td>
                    <td><span className="text-xs font-mono text-green-400">{tx.receiver_wallet || tx.receiver_id?.slice(0, 8) || "—"}</span></td>
                    <td className="text-white font-semibold text-sm">${fmt(tx.amount)}</td>
                    <td className="text-yellow-400 text-xs">${fmt(tx.fee)}</td>
                    <td>
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_COLOR[tx.status] || "text-gray-400 bg-white/5 border-white/10"}`}>
                        {STATUS_LABEL[tx.status] || tx.status}
                      </span>
                    </td>
                    <td className="text-gray-500 text-xs">{fmtDate(tx.created_at)}</td>
                  </tr>
                ))}
                {filteredTxs.length === 0 && (
                  <tr><td colSpan={8} className="text-center text-gray-500 py-12">No hay transacciones</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Disputes */}
      {tab === "disputes" && (
        <div className="space-y-4">
          <div className="flex gap-2">
            {["all", "open", "resolved"].map(f => (
              <button key={f} onClick={() => setDisputeFilter(f)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${disputeFilter === f ? "bg-red-500/20 text-red-400" : "text-gray-400 hover:bg-white/5"}`}>
                {f === "all" ? "Todas" : f === "open" ? "Abiertas" : "Resueltas"}
              </button>
            ))}
          </div>
          <div className="space-y-3">
            {filteredDisputes.map(d => (
              <div key={d.id} className="glass-panel p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${d.status === "open" ? "bg-orange-500/10 text-orange-400 border-orange-500/20" : "bg-green-500/10 text-green-400 border-green-500/20"}`}>
                        {d.status === "open" ? "Abierta" : "Resuelta"}
                      </span>
                      <span className="text-gray-500 text-xs">{fmtDate(d.created_at)}</span>
                    </div>
                    <p className="text-white text-sm font-medium">{d.reason}</p>
                    {d.transaction && (
                      <p className="text-gray-500 text-xs mt-1">
                        ${fmt(d.transaction.amount)} · {d.transaction.sender_wallet || "—"} → {d.transaction.receiver_wallet || "—"}
                      </p>
                    )}
                    {d.resolution && <p className="text-gray-400 text-xs mt-1 italic">Resolución: {d.resolution}</p>}
                  </div>
                  {d.status === "open" && (
                    <button onClick={() => setSelectedDispute(d)}
                      className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg text-xs font-medium transition-colors border border-red-500/30">
                      <ChevronRight size={13} />Resolver
                    </button>
                  )}
                </div>
              </div>
            ))}
            {filteredDisputes.length === 0 && (
              <div className="glass-panel p-16 text-center">
                <Flag size={32} className="text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No hay disputas {disputeFilter !== "all" ? disputeFilter === "open" ? "abiertas" : "resueltas" : ""}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {showConfig && <ConfigPanel onClose={() => { setShowConfig(false); load(); }} />}
      {selectedDispute && (
        <DisputeModal
          dispute={selectedDispute}
          onClose={() => setSelectedDispute(null)}
          onResolve={resolveDispute}
        />
      )}
    </div>
  );
}
